import { motion } from "framer-motion";
import { Factory, BadgeIndianRupee, Warehouse, ShoppingBag, Repeat, Landmark } from "lucide-react";

const ITEMS = [
  {
    icon: Factory,
    title: "Direct Sourcing",
    copy: "Products sourced directly from manufacturers — no middle layers between the people who make your supplies and you.",
  },
  {
    icon: BadgeIndianRupee,
    title: "Fair Pricing",
    copy: "The price you see is the PAQVERO price — same product, same variant, same quantity, same price. No haggling required.",
  },
  {
    icon: Warehouse,
    title: "Ready Stock",
    copy: "Products stocked in PAQVERO warehouses and ready to order the moment you need them.",
  },
  {
    icon: ShoppingBag,
    title: "One-Stop Shop",
    copy: "Packaging, shipping supplies and business essentials in one place.",
  },
  {
    icon: Repeat,
    title: "Easy Reordering",
    copy: "Find your regular supplies and reorder them in a few clicks.",
  },
  {
    icon: Landmark,
    title: "PAQVERO Credit",
    copy: "Approved business customers can purchase now and pay within their agreed credit terms.",
    chip: "Up to 30-day terms",
  },
];

export default function WhyUs() {
  return (
    <section id="about" data-testid="why-us-section" className="py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="mb-10"
        >
          <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            Why Paqvero
          </p>
          <h2 className="font-display max-w-2xl text-3xl font-semibold tracking-tight text-1 sm:text-4xl">
            Why Businesses Buy From PAQVERO.
          </h2>
        </motion.div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ITEMS.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
              data-testid={`why-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative overflow-hidden rounded-xl border border-line bg-surface p-5 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/25 hover:shadow-lift"
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-emerald-500/[0.05] blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
              <div className="flex items-center gap-3.5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-hover text-emerald-500">
                  <item.icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                </span>
                <h3 className="font-display text-lg font-semibold tracking-tight text-1">
                  {item.title}
                </h3>
              </div>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-2">
                {item.copy}
              </p>
              {item.chip && (
                <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/[0.08] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-500">
                  <span className="h-1.5 w-1.5 animate-blink rounded-full bg-emerald-500" />
                  {item.chip}
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
